import React, { useCallback, useMemo } from "react";
import { ReactSelect } from "@services/ui-components";
import { OptionString } from "../../../../interfaces/interfaces";
import { LanguageVersions } from "../../../../interfaces/meMenuInterfaces/interfaces";

type LanguageDto = LanguageVersions | null | undefined;

interface MenuLanguageVersionSelectProps {
    id?: string;
    languages: LanguageDto[];
    primaryLanguageCode: string | null;
    primaryLanguageName: string | null;
    currentLanguageCode: string | null;
    addLanguageValue: string;
    addLanguageLabel: string;
    showAddLanguageOption?: boolean;
    primaryLabel: string;
    onChangeValue: (nextValue: string) => void;
    isDisabled?: boolean;
    isLoading?: boolean;
}

const MenuLanguageVersionSelect: React.FC<MenuLanguageVersionSelectProps> = ({
    id = "menu-language-version-select",
    languages,
    primaryLanguageCode,
    primaryLanguageName,
    currentLanguageCode,
    addLanguageValue,
    addLanguageLabel,
    showAddLanguageOption = true,
    primaryLabel,
    onChangeValue,
    isDisabled,
    isLoading,
}) => {
    const options: OptionString[] = useMemo(() => {
        const baseOptions: OptionString[] = (languages ?? [])
            .filter((language) => Boolean(language?.isoCode))
            .map((language) => {
                const isoCode = String(language!.isoCode).trim();
                const languageName =
                    (isoCode === primaryLanguageCode
                        ? language?.name?.trim() || primaryLanguageName
                        : language?.name?.trim()) || isoCode;
                return { value: isoCode, label: languageName };
            });

        if (primaryLanguageCode && !baseOptions.some((option) => option.value === primaryLanguageCode)) {
            baseOptions.unshift({ value: primaryLanguageCode, label: primaryLanguageName || primaryLanguageCode });
        }

        baseOptions.sort((a, b) => {
            const aIsPrimary = a.value === primaryLanguageCode;
            const bIsPrimary = b.value === primaryLanguageCode;

            if (aIsPrimary) return -1;
            if (bIsPrimary) return 1;
            return a.label.localeCompare(b.label);
        });

        const addLanguageOption: OptionString = {
            value: addLanguageValue,
            label: addLanguageLabel,
        };

        if (!showAddLanguageOption) return baseOptions;

        return [addLanguageOption, ...baseOptions];
    }, [
        languages,
        primaryLanguageCode,
        primaryLanguageName,
        addLanguageValue,
        addLanguageLabel,
        showAddLanguageOption,
    ]);

    const selectedOption = useMemo(() => {
        return options.find((option) => option.value === currentLanguageCode) ?? null;
    }, [options, currentLanguageCode]);

    const formatOptionLabel = useCallback(
        (option: OptionString) => {
            if (option.value === addLanguageValue) {
                return <span className="text-primary">+ {option.label}</span>;
            }

            if (primaryLanguageCode && option.value === primaryLanguageCode) {
                return (
                    <span className="d-flex align-items-center gap-2">
                        <span>{option.label}</span>
                        <span className="text-grey">({primaryLabel})</span>
                    </span>
                );
            }

            return option.label;
        },
        [addLanguageValue, primaryLanguageCode, primaryLabel]
    );

    const handleChange = useCallback(
        (nextOption: OptionString | null) => {
            const nextValue = nextOption?.value;
            if (!nextValue) return;
            onChangeValue(nextValue);
        },
        [onChangeValue]
    );

    return (
        <ReactSelect
            id={id}
            value={selectedOption}
            options={options}
            onChange={handleChange}
            isSearchable
            disabled={Boolean(isDisabled || isLoading)}
            placeholder=""
            isMeMenu
            isTitle
            formatOptionLabel={formatOptionLabel}
            isLoading={isLoading}
        />
    );
};

export default MenuLanguageVersionSelect;
